import React, { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { components } from "../../types/api";
import { SheetTarget } from "./DashboardSheets";
import { MonthlyBarChart, monthLabel } from "./DashboardCharts";
import { BlockGrid, Empty, QualityChip, Section } from "./DashboardBits";
import { euro } from "./dashboardFormat";

type MonthlyBilling = components["schemas"]["MonthlyBilling"];
type BillingProposal = components["schemas"]["BillingProposal"];

/** Los cortes del filtro de probabilidad mínima, como en la especificación. */
const PROBABILITY_FLOORS = [0, 25, 50, 75, 90, 95];

/**
 * Las entregadas que pasan el filtro. Con "Todas" entran también las que no tienen
 * probabilidad en Twenty; con un mínimo, solo las que la tienen y llegan a él.
 */
export const includedOpen = (open: BillingProposal[], floor: number) =>
  floor === 0
    ? open
    : open.filter(
        (item) => item.probability !== null && item.probability >= floor
      );

/** Ponderado (importe × probabilidad) y el resto hasta el 100 %, por mes. */
export const openSeries = (included: BillingProposal[], months: number) => {
  const weighted = Array(months).fill(0);
  const rest = Array(months).fill(0);
  included.forEach((item) => {
    const probability = (item.probability ?? 0) / 100;
    item.perMonth.forEach((value, index) => {
      weighted[index] += value * probability;
      rest[index] += value * (1 - probability);
    });
  });
  return { weighted, rest };
};

const ProposalLine: React.FC<{
  item: BillingProposal;
  onOpen: (target: SheetTarget) => void;
}> = ({ item, onOpen }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    gap={1}
    py={0.25}
    role="button"
    tabIndex={0}
    sx={{ cursor: "pointer" }}
    onClick={() => onOpen({ type: "proposal", id: item.id })}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen({ type: "proposal", id: item.id });
      }
    }}
  >
    <Typography variant="body2">
      {item.clientName}{" "}
      <Typography
        component="span"
        variant="caption"
        sx={{ fontFamily: "monospace" }}
      >
        {item.proposalNumber}
      </Typography>
    </Typography>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ whiteSpace: "nowrap" }}
    >
      {item.probability === null ? "sin probabilidad" : `${item.probability} %`}{" "}
      · {monthLabel(item.startMonth)} · {item.durationMonths}{" "}
      {item.durationMonths === 1 ? "mes" : "meses"} · {euro.format(item.amount)}
    </Typography>
  </Stack>
);

interface Props {
  billing: MonthlyBilling;
  onOpen: (target: SheetTarget) => void;
}

/**
 * Facturación cerrada por mes y facturación posible de propuestas abiertas. Hoy con el
 * presupuesto orientativo repartido entre los meses de ejecución estimados; por eso todo
 * va rayado y sin línea de beneficio bruto, que solo existirá con la valoración.
 */
const BillingCharts: React.FC<Props> = ({ billing, onOpen }) => {
  const theme = useTheme();
  const [floor, setFloor] = useState(0);
  const included = includedOpen(billing.open, floor);
  const { weighted, rest } = openSeries(included, billing.months.length);
  const total = (values: number[]) =>
    values.reduce((sum, value) => sum + value, 0);
  const withoutProbability = included.filter(
    (item) => item.probability === null
  ).length;

  return (
    <BlockGrid>
      <Section
        title="Facturación cerrada por mes"
        subtitle="Propuestas aceptadas, repartidas entre sus meses de ejecución (próximos 6 meses)"
        action={<QualityChip />}
      >
        {billing.closedProposals.length === 0 ? (
          <Empty>
            No hay propuestas aceptadas que se ejecuten en los próximos 6 meses.
          </Empty>
        ) : (
          <>
            <MonthlyBarChart
              months={billing.months}
              series={[
                {
                  key: "closed",
                  label: `Aceptado · ${euro.format(total(billing.closed))}`,
                  color: theme.palette.primary.main,
                  values: billing.closed,
                },
              ]}
            />
            <Box mt={1}>
              {billing.closedProposals.map((item) => (
                <ProposalLine key={item.id} item={item} onOpen={onOpen} />
              ))}
            </Box>
          </>
        )}
      </Section>

      <Section
        title="Facturación posible de propuestas abiertas"
        subtitle="Entregadas pendientes de respuesta: la parte ponderada y el resto hasta el 100 %"
        action={
          <Stack direction="row" gap={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="billing-probability">Probabilidad</InputLabel>
              <Select
                labelId="billing-probability"
                label="Probabilidad"
                value={floor}
                onChange={(event) => setFloor(Number(event.target.value))}
              >
                {PROBABILITY_FLOORS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value === 0 ? "Todas" : `≥ ${value} %`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <QualityChip />
          </Stack>
        }
      >
        {included.length === 0 ? (
          <Empty>
            {billing.open.length === 0
              ? "No hay propuestas entregadas que se ejecuten en los próximos 6 meses."
              : "Ninguna entregada llega a esa probabilidad."}
          </Empty>
        ) : (
          <>
            <MonthlyBarChart
              months={billing.months}
              series={[
                {
                  key: "weighted",
                  label: `Ponderado · ${euro.format(total(weighted))}`,
                  color: theme.palette.primary.main,
                  values: weighted,
                },
                {
                  key: "rest",
                  label: `Resto hasta el 100 % · ${euro.format(total(rest))}`,
                  color: theme.palette.grey[500],
                  values: rest,
                },
              ]}
            />
            {withoutProbability > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {withoutProbability}{" "}
                {withoutProbability === 1
                  ? "propuesta no tiene"
                  : "propuestas no tienen"}{" "}
                probabilidad en Twenty: cuentan enteras en el resto.
              </Typography>
            )}
            <Typography variant="subtitle2" mt={1}>
              Propuestas incluidas
            </Typography>
            {included.map((item) => (
              <ProposalLine key={item.id} item={item} onOpen={onOpen} />
            ))}
          </>
        )}
      </Section>
    </BlockGrid>
  );
};

export default BillingCharts;
